import './PdfLibraryDrawer.css';
import type { PdfMeta } from '../../services/host/PdfServiceModule';
import type { PdfLibraryState } from '../../hooks/usePdfLibrary';

/**
 * V7.2.B PDF library drawer (2026-06-03) — 7th drawer panel.
 *
 * Lists every PDF in the manifest grouped by category (manuals, modules,
 * references, character sheets). Each row is a clickable button that
 * fires onSelect(path) — the ScenarioRunner host wires the click into
 * a PdfViewerModal mount that fetches the PDF as a blob and renders it
 * in an iframe.
 *
 * 3-state union per usePdfLibrary (R1.0 Concern 2): renders loading
 * affordance when state==='loading', error message when state==='error',
 * and the grouped list when state==='ready'.
 *
 * Files larger than 100 MB get a size-warning badge (Subagent D flagged
 * Villains {VG35011}.pdf at 140 MB — 3× any other file).
 */

const LARGE_FILE_THRESHOLD_MB = 100;

export interface PdfLibraryDrawerProps {
  pdfs: PdfMeta[];
  state: PdfLibraryState;
  error?: string | null;
  onSelect: (path: string) => void;
  testidPrefix?: string;
}

const CATEGORY_ORDER: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'manual', label: 'Manuals' },
  { key: 'reference', label: 'References' },
  { key: 'module', label: 'Modules' },
  { key: 'character-sheet', label: 'Character Sheets' },
  { key: 'other', label: 'Other' },
];

function groupByCategory(pdfs: PdfMeta[]): Map<string, PdfMeta[]> {
  const groups = new Map<string, PdfMeta[]>();
  for (const pdf of pdfs) {
    const list = groups.get(pdf.category) ?? [];
    list.push(pdf);
    groups.set(pdf.category, list);
  }
  return groups;
}

export function PdfLibraryDrawer({
  pdfs,
  state,
  error,
  onSelect,
  testidPrefix = 'br-scenario-drawer-pdf-library',
}: PdfLibraryDrawerProps) {
  return (
    <aside
      className="br-scenario-drawer br-pdf-library-drawer"
      data-testid={testidPrefix}
      aria-label="PDF library"
    >
      <header>
        <h2 className="br-display">PDF Library</h2>
      </header>

      {state === 'loading' && (
        <p
          className="br-pdf-library-drawer-loading"
          data-testid={`${testidPrefix}-loading`}
        >
          Loading PDF manifest…
        </p>
      )}

      {state === 'error' && (
        <p
          className="br-pdf-library-drawer-error"
          data-testid={`${testidPrefix}-error`}
        >
          Unable to load PDFs: {error ?? 'unknown error'}
        </p>
      )}

      {state === 'ready' && pdfs.length === 0 && (
        <p
          className="br-pdf-library-drawer-empty"
          data-testid={`${testidPrefix}-empty`}
        >
          No PDFs available.
        </p>
      )}

      {state === 'ready' &&
        pdfs.length > 0 &&
        CATEGORY_ORDER.map(({ key, label }) => {
          const groups = groupByCategory(pdfs);
          const entries = groups.get(key);
          if (!entries || entries.length === 0) {
            return null;
          }
          return (
            <section key={key}>
              <h3
                className="br-pdf-library-drawer-section-header"
                data-testid={`${testidPrefix}-section-${key}`}
              >
                {label} ({entries.length})
              </h3>
              <ul className="br-pdf-library-drawer-list">
                {entries.map((pdf) => (
                  <li key={pdf.path}>
                    <button
                      type="button"
                      className="br-pdf-library-drawer-item"
                      data-testid={`${testidPrefix}-item-${pdf.path}`}
                      onClick={() => onSelect(pdf.path)}
                    >
                      <span className="br-pdf-library-drawer-item-title">
                        {pdf.title}
                      </span>
                      <span
                        className={
                          pdf.size_mb > LARGE_FILE_THRESHOLD_MB
                            ? 'br-pdf-library-drawer-size-warn'
                            : 'br-pdf-library-drawer-size'
                        }
                        data-testid={
                          pdf.size_mb > LARGE_FILE_THRESHOLD_MB
                            ? `${testidPrefix}-warn-${pdf.path}`
                            : undefined
                        }
                      >
                        {pdf.size_mb} MB
                        {pdf.size_mb > LARGE_FILE_THRESHOLD_MB && ' — large file'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
    </aside>
  );
}

export default PdfLibraryDrawer;
